// T2T-05 — Chat / message API wrappers.

import { getData, postData, putData, deleteData, ServerURL } from "../FetchApiServices";
import { getCurrentToken } from "./auth";

const REQ = (id) => `t2t/requests/${encodeURIComponent(id)}`;

// T2T-05-C4: SSE URL builders. REST calls in this app go directly to the
// backend at ServerURL (http://localhost:5500/api) — they bypass the CRA
// proxy. We follow the same pattern so SSE reaches the backend reliably.
// Backend CORS (cors()) already allows the dev origin, and EventSource only
// makes a simple GET — no preflight needed. Token rides as ?access_token=
// because EventSource cannot send custom headers.
function buildSseUrl(suffix) {
  const token = getCurrentToken() || "";
  // Strip trailing slash from base, leading slash from suffix
  const base = (ServerURL || "").replace(/\/$/, "");
  const path = suffix.replace(/^\//, "");
  const sep  = path.includes("?") ? "&" : "?";
  return token
    ? `${base}/${path}${sep}access_token=${encodeURIComponent(token)}`
    : `${base}/${path}`;
}

export const buildChatStreamUrl = (requestId) =>
  buildSseUrl(`${REQ(requestId)}/messages/stream`);

export const buildChatActivityStreamUrl = () =>
  buildSseUrl(`t2t/chat-activity-sse`);

export const sendTypingPing = (requestId) =>
  postData(`${REQ(requestId)}/messages/typing`, {});

// T2T-05-C6: vendor chat token admin (per-request)
export const listVendorChatTokens = (requestId) =>
  getData(`${REQ(requestId)}/vendor-chat-tokens`);

export const issueVendorChatToken = (requestId, body) =>
  postData(`${REQ(requestId)}/vendor-chat-tokens`, body);

export const revokeVendorChatToken = (requestId, tokenId) =>
  postData(`${REQ(requestId)}/vendor-chat-tokens/${tokenId}/revoke`, {});

// ─── Messages ────────────────────────────────────────────────────────────
export const fetchMessages = (requestId, params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null))
  ).toString();
  return getData(`${REQ(requestId)}/messages${q ? `?${q}` : ""}`);
};

export const sendMessage = (requestId, body) =>
  postData(`${REQ(requestId)}/messages`, body);

export const editMessage = (requestId, messageId, body) =>
  putData(`${REQ(requestId)}/messages/${messageId}`, body);

export const deleteMessage = (requestId, messageId) =>
  deleteData(`${REQ(requestId)}/messages/${messageId}`);

export const markMessagesRead = (requestId) =>
  postData(`${REQ(requestId)}/messages/mark-read`, {});

export const fetchUnreadCount = (requestId) =>
  getData(`${REQ(requestId)}/messages/unread-count`);

// ─── Participants / mention picker ───────────────────────────────────────
export const fetchParticipants = (requestId) =>
  getData(`${REQ(requestId)}/participants`);

export const fetchMentionable = (requestId, q = "", limit = 20) => {
  const params = new URLSearchParams();
  if (q)     params.set("q", q);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return getData(`${REQ(requestId)}/participants/mentionable${qs ? `?${qs}` : ""}`);
};

// ─── Attachments ─────────────────────────────────────────────────────────
// 3-step upload: presign → PUT to S3 → register → bind via attachment_ids[].

export const presignAttachment = (requestId, body) =>
  postData(`${REQ(requestId)}/messages/attachments/presign`, body);

export const registerAttachment = (requestId, body) =>
  postData(`${REQ(requestId)}/messages/attachments`, body);

export const fetchAttachmentDownloadUrl = (attachmentId) =>
  getData(`t2t/chat-attachments/${attachmentId}/download`);

export const deleteAttachment = (attachmentId) =>
  deleteData(`t2t/chat-attachments/${attachmentId}`);

// T2T-05-C7: per-thread mute toggle.
export const fetchMuteStatus = (requestId) =>
  getData(`${REQ(requestId)}/messages/mute`);

export const muteThread = (requestId) =>
  postData(`${REQ(requestId)}/messages/mute`, {});

export const unmuteThread = (requestId) =>
  deleteData(`${REQ(requestId)}/messages/mute`);

// T2T-05-C7: chat thread PDF export — returns a download URL we can hit in a
// new tab with the auth token in a query param. Admin-only (backend gates).
export const buildChatPdfExportUrl = (requestId) =>
  buildSseUrl(`${REQ(requestId)}/messages/export.pdf`);

// Direct PUT to a pre-signed S3 URL — bypasses our axios instance because
// signing requires a clean request without our auth interceptor.
export async function uploadFileToS3(presignedUrl, file, contentType) {
  const resp = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`S3 upload failed: ${resp.status} ${text || resp.statusText}`);
  }
  return true;
}
